import {
  trigger,
  transition,
  animate,
  style,
  animation,
  useAnimation,
  query,
  group,
  AnimationTriggerMetadata,
  AnimationStyleMetadata,
  AnimationAnimateRefMetadata,
} from '@angular/animations';

const timing = '200ms ease-in';
const optional = { optional: true };
const distances = {
  normalX: 'translateX(0%)',
  right: 'translateX(100%)',
  left: 'translateX(-100%)',
  normalY: 'translateY(0%)',
  bottom: 'translateY(100%)',
};
const resetAnimatableView = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  display: 'block',
  top: 0,
};

/**
 * Creates an animation style with reset positioning for 2D transforms.
 *
 * @param from - The starting transform value (e.g., `'translateX(-100%)'`)
 * @returns An {@link AnimationStyleMetadata} with absolute positioning and the specified transform
 *
 * @remarks
 * Used internally to set up reusable animation styles that combine positioning with transforms.
 * The style ensures the element is absolutely positioned and takes up full dimensions.
 *
 * @example
 *
 * ```typescript
 * const animation = resetableTransform('translateX(-100%)'); // returns an AnimationStyleMetadata
 * ```
 */
export function resetableTransform(from: string): AnimationStyleMetadata {
  return style({
    ...resetAnimatableView,
    transform: from,
  });
}

/**
 * Creates a 2D slide animation effect from one position to another.
 *
 * @param from - The starting transform position (e.g., `'translateX(-100%)'`)
 * @param to - The ending transform position (e.g., `'translateX(0%)'`)
 * @returns An {@link AnimationAnimateRefMetadata} defining the slide animation sequence
 *
 * @remarks
 * Reusable animation that combines an initial transform with a 200ms animation to the target position.
 * Used as a building block for more complex animations like slides and transitions.
 *
 * @example
 *
 * ```typescript
 * const animation = slide2D('translateX(-100%)', 'translateX(0%)'); // returns an AnimationAnimateRefMetadata
 * ```
 */
export function slide2D(from: string, to: string): AnimationAnimateRefMetadata {
  return useAnimation(animation([resetableTransform(from), animate(timing, style({ transform: to }))]));
}

/**
 * Animation trigger for vertical slide-up/down transitions.
 *
 * @remarks
 * Applied with `@slideUp` trigger on Angular components. Slides elements up when entering
 * (`:enter`) and down when leaving (`:leave`). Default timing is 200ms with ease-in.
 *
 * @example
 *
 * ```html
 * <div @slideUp>
 *   <p>Content that slides in from bottom</p>
 * </div>
 * ```
 */
export const slideUpAnimation: AnimationTriggerMetadata = trigger('slideUp', [
  transition(':enter', [slide2D(distances.bottom, distances.normalY)]),
  transition(':leave', [slide2D(distances.normalY, distances.bottom)]),
]);

/**
 * Animation trigger for horizontal left/right slide transitions.
 *
 * @remarks
 * Applied with `@slideX` trigger on Angular components. Handles directional slides:
 * - Slides from right when entering with `'isLeft'` state
 * - Slides from left when entering with `'isRight'` state
 * Provides responsive horizontal navigation animations.
 *
 * @example
 *
 * ```html
 * <div [@slideX]="direction">
 *   <p>Content slides horizontally based on direction</p>
 * </div>
 * ```
 */
export const slideXAnimation: AnimationTriggerMetadata = trigger('slideX', [
  transition('void => isLeft', [slide2D(distances.right, distances.normalX)]),
  transition('void => isRight', [slide2D(distances.left, distances.normalX)]),
  transition('isRight => void', [slide2D(distances.normalX, distances.left)]),
  transition('isLeft => void', [slide2D(distances.normalX, distances.right)]),
]);

/**
 * Animation trigger for tab navigation transitions.
 *
 * @remarks
 * Applied with `@tabsAnimation` trigger on tab components. Supports bidirectional sliding:
 * - `:decrement` transitions (previous tab) slide left to right
 * - `:increment` transitions (next tab) slide right to left
 * Creates smooth carousel-like navigation effects between tabs.
 *
 * @example
 *
 * ```html
 * <div [@tabsAnimation]="selectedTabIndex">
 *   <div>Tab 1 content</div>
 *   <div>Tab 2 content</div>
 * </div>
 * ```
 */
export const tabsAnim: AnimationTriggerMetadata = trigger('tabsAnimation', [
  // Previous, slide left to right to show left tab
  transition(':decrement', [
    // set new tab X location to be -100%
    query(':enter', resetableTransform(distances.left)),

    group([
      // slide existing page from 0% to 100% to the right
      query(':leave', animate(timing, resetableTransform(distances.right)), optional),
      // slide new page from -100% to 0% to the right
      query(
        ':enter',
        animate(
          timing,
          style({
            opacity: 1,
            transform: distances.normalX,
          }),
        ),
        optional,
      ),
    ]),
  ]),
  // Next, slide right to left to show right page
  transition(':increment', [
    // set new page X location to be 100%
    query(':enter', resetableTransform(distances.right)),

    group([
      // slide existing page from 0% to -100% to the left
      query(':leave', animate(timing, resetableTransform(distances.left))),
      // slide new page from 100% to 0% to the left
      query(
        ':enter',
        animate(
          timing,
          style({
            opacity: 1,
            transform: distances.normalX,
          }),
        ),
      ),
    ]),
  ]),
]);

/**
 * Animation trigger for fade-slide combined entry/exit effects.
 *
 * @remarks
 * Applied with `@fadeSlideInOut` trigger on notification components. Animates elements
 * with simultaneous fade and vertical translation:
 * - Enter: fade from 0 opacity while sliding up from -10px
 * - Leave: fade to 0 opacity while sliding up -10px
 * Ideal for notification and toast message animations.
 *
 * @example
 *
 * ```html
 * <div @fadeSlideInOut>
 *   <bb-notification message="Success" />
 * </div>
 * ```
 */
export const fadeSlideAnimation: AnimationTriggerMetadata = trigger('fadeSlideInOut', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(-10px)' }),
    animate(timing, style({ opacity: 1, transform: 'translateY(0)' })),
  ]),
  transition(':leave', [animate(timing, style({ opacity: 0, transform: 'translateY(-10px)' }))]),
]);

/**
 * Animation trigger for fade in/out transitions.
 *
 * @remarks
 * Applied with `@fadeIn` trigger on Angular components. Provides simple opacity-based
 * animations without positional changes:
 * - Enter: fade from 0 to 1 opacity
 * - Leave: fade from 1 to 0 opacity
 * Suitable for general-purpose visibility transitions.
 *
 * @example
 *
 * ```html
 * <div @fadeIn>
 *   <p>Content fades in and out</p>
 * </div>
 * ```
 */
export const fadeInAnimation: AnimationTriggerMetadata = trigger('fadeIn', [
  transition(':enter', [
    style({
      opacity: 0,
    }),
    animate(timing, style({ opacity: 1 })),
  ]),
  transition(':leave', [animate(timing, style({ opacity: 0 }))]),
]);

/**
 * Animation trigger for route-based page transitions.
 *
 * @remarks
 * Applied with `@routeAnimations` trigger on page containers. Animates route changes with
 * fade-slide effects:
 * - Entering pages slide up from -50px while fading in
 * - Exiting pages move off-screen while fading out
 * Creates smooth transitions between different application pages.
 *
 * @example
 *
 * ```html
 * <div [@routeAnimations]="routeAnimationState">
 *   <router-outlet></router-outlet>
 * </div>
 * ```
 */
export const routerAnim: AnimationTriggerMetadata = trigger('routeAnimations', [
  transition('* <=> *', [
    style({ position: 'relative' }),
    query(
      ':enter, :leave',
      [
        style({
          ...resetAnimatableView,
          transform: 'translateY(-50px)',
          opacity: 0,
        }),
      ],
      optional,
    ),
    query(':enter', [animate(timing, style({ transform: distances.normalY, opacity: 1 }))], optional),
  ]),
]);

/**
 * Animation trigger for error message display with height expansion.
 *
 * @remarks
 * Applied with `@errorMessageAnimation` trigger on error message components.
 * Animates error visibility with both opacity and height transitions:
 * - Entry: expands from 0 height while fading in (150ms)
 * - Exit: collapses to 0 height while fading out
 * Provides smooth reveal/hide effects for validation messages.
 *
 * @example
 *
 * ```html
 * <div @errorMessageAnimation>
 *   <span class="error-text">This field is required</span>
 * </div>
 * ```
 */
export const errorMessageAnimation = trigger('errorMessageAnimation', [
  transition('void => *', [style({ opacity: 0, height: '0px' }), animate(150, style({ opacity: 1, height: '*' }))]),
]);
